import {FC, ReactNode} from 'react';

import RatingStarIcon from '@/assets/icons/rating-star.svg?react';
import AppLogo from '@/shared/components/AppLogo';
import {localize} from '@/shared/services/i18n';
import {localizeHref} from '@/sync/lib/localizeHref';

interface AuthSectionProps {
  children?: ReactNode;
  langSwitcher?: ReactNode;
}

const AuthSection: FC<AuthSectionProps> = ({children, langSwitcher}) => {
  return (
    <section className="relative">
      <picture>
        <source
          media="(max-width: 640px)"
          srcSet="/images/login/top_mob.webp"
        />
        <img
          src="/images/login/top_desk.webp"
          alt="welcome"
          className="absolute top-0 left-0 h-full w-full object-cover"
        />
      </picture>
      <div className="relative z-10 min-h-svh select-none text-secondary">
        <div className="absolute top-5 right-0 left-0 mx-4 flex items-center justify-between sm:mx-11">
          <AppLogo className="h-11.25 w-18 text-white sm:top-4 sm:left-11 sm:h-15.75 sm:w-30" />
          {langSwitcher}
        </div>
        <div className="mx-auto flex min-h-svh w-full max-w-7xl px-4 sm:px-10">
          <div className="flex min-h-svh w-full flex-col items-center justify-end gap-5 text-center sm:w-125 sm:items-start sm:gap-8 sm:text-left">
            <h1 className="text-center font-bold text-[clamp(1.75rem,7vw,2rem)]/[140%] text-secondary sm:mt-[18dvh] sm:max-w-125 sm:text-left sm:text-7xl/[140%] sm:[text-shadow:0_4px_4px_rgba(0,0,0,0.25)]">
              {localize('sign_promo')}
            </h1>
            <div className="flex items-center gap-4">
              <div className="flex flex-col items-center gap-1">
                <span className="font-bold text-xl sm:text-3xl">35M+</span>
                <span className="sm:text-xl">
                  {localize('social_proof_downloads_label')}
                </span>
              </div>
              <div className="h-10 w-px bg-white" />
              <div className="flex flex-col items-center gap-1">
                <span className="flex items-center gap-1 font-bold text-xl sm:gap-1.5 sm:text-3xl">
                  4.6 <RatingStarIcon className="w-4 sm:w-5" />
                </span>
                <span className="sm:text-xl">
                  {localize('social_proof_reviews_label')}
                </span>
              </div>
            </div>
            <div className="flex flex-col items-center gap-2.5 sm:items-start">
              <div className="flex min-h-42.5 flex-col items-center justify-end gap-2.5">
                {children}
              </div>
              <div className="mt-2.5 mb-6 text-xs sm:mb-16 sm:text-sm">
                {localize('sign_terms_agree')}
                <a
                  href={localizeHref('/:lang?/terms')}
                  className="cursor-pointer font-normal text-white hover:text-white"
                  target="_blank"
                  rel="noreferrer"
                >
                  {localize('sign_terms_link')}
                </a>
                ,&nbsp;
                <div>
                  <a
                    href={localizeHref('/:lang?/privacy')}
                    className="cursor-pointer font-normal text-white hover:text-white"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {localize('_privacy_policy_instrumental')}
                  </a>
                  {localize('legal_links_and')}
                  <a
                    href={localizeHref('/:lang?/community-guidelines')}
                    className="cursor-pointer font-normal text-white hover:text-white"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {localize('community_guidelines_link_instrumental')}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AuthSection;
